import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  ADD_CUSTOM_WORK_ORDER_FLAG,
  GET_ACTIVE_WORK_ORDER_FLAG_TEMPLATES,
  GET_CUSTOM_FLAGS_FOR_WORK_ORDER,
  REMOVE_CUSTOM_WORK_ORDER_FLAG
} from '../../graphql/work-order-flags';
import { CustomFlagIndicator } from '../job-indicators';
import { FlagIcon } from '../../utils/flag-icons';
import { calculateTintedFlagTextColor, sortCustomFlags } from '../../utils/custom-flag-utils';
import { useAuth } from '../../contexts/AuthContext';

const CAN_MANAGE_WORK_ORDER_FLAGS = new Set([
  'ADMIN',
  'PROPERTY_MANAGER',
  'HOUSEKEEPER_MANAGER',
  'CALL_CENTER',
  'MAINTENANCE'
]);

export default function WorkOrderFlagManager({ workOrder, onFlagChange }) {
  const { user } = useAuth();
  const canManage = CAN_MANAGE_WORK_ORDER_FLAGS.has(user?.role);
  const [isExpanded, setIsExpanded] = useState(false);
  const [newFlag, setNewFlag] = useState({
    flagType: 'custom',
    flagValue: '',
    flagColor: '#3b82f6'
  });

  const { data: flagsData, refetch: refetchFlags } = useQuery(GET_CUSTOM_FLAGS_FOR_WORK_ORDER, {
    variables: { workOrderId: workOrder?.id },
    skip: !workOrder?.id
  });

  const { data: templatesData } = useQuery(GET_ACTIVE_WORK_ORDER_FLAG_TEMPLATES, {
    skip: !workOrder?.id
  });

  const [addFlag, { loading: adding }] = useMutation(ADD_CUSTOM_WORK_ORDER_FLAG, {
    refetchQueries: [{ query: GET_CUSTOM_FLAGS_FOR_WORK_ORDER, variables: { workOrderId: workOrder?.id } }],
    awaitRefetchQueries: true
  });
  const [removeFlag, { loading: removing }] = useMutation(REMOVE_CUSTOM_WORK_ORDER_FLAG, {
    refetchQueries: [{ query: GET_CUSTOM_FLAGS_FOR_WORK_ORDER, variables: { workOrderId: workOrder?.id } }],
    awaitRefetchQueries: true
  });

  const customFlags = sortCustomFlags(flagsData?.getCustomFlagsForWorkOrder || workOrder?.customFlags || []);
  const templates = templatesData?.getActiveWorkOrderFlagTemplates || [];

  const availableTemplates = useMemo(() => {
    return templates.filter(template => !customFlags.some(flag =>
      flag.templateId === template.id ||
      (flag.flagType === 'custom' && flag.flagValue === template.flagValue)
    ));
  }, [customFlags, templates]);

  const handleToggleExpanded = () => {
    setIsExpanded(prev => !prev);
  };

  const handleAddTemplateFlag = async (template) => {
    try {
      await addFlag({
        variables: {
          input: {
            workOrderId: workOrder.id,
            templateId: template.id,
            flagType: 'custom',
            flagValue: template.flagValue,
            flagColor: template.backgroundColor,
            sortOrder: customFlags.length
          }
        }
      });
      await refetchFlags();
      onFlagChange?.();
      toast.success('Flag added');
    } catch (error) {
      toast.error(error.message || 'Failed to add flag');
    }
  };

  const handleAddManualFlag = async () => {
    if (!newFlag.flagValue.trim()) return;

    try {
      await addFlag({
        variables: {
          input: {
            workOrderId: workOrder.id,
            flagType: newFlag.flagType,
            flagValue: newFlag.flagValue.trim(),
            flagColor: newFlag.flagColor,
            sortOrder: customFlags.length
          }
        }
      });
      setNewFlag({ flagType: 'custom', flagValue: '', flagColor: '#3b82f6' });
      await refetchFlags();
      onFlagChange?.();
      toast.success('Flag added');
    } catch (error) {
      toast.error(error.message || 'Failed to add flag');
    }
  };

  const handleRemove = async (flag) => {
    if (!window.confirm(`Remove "${flag.flagValue}" from this work order?`)) return;

    try {
      await removeFlag({ variables: { input: { id: flag.id } } });
      await refetchFlags();
      onFlagChange?.();
      toast.success('Flag removed');
    } catch (error) {
      toast.error(error.message || 'Failed to remove flag');
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        type="button"
        onClick={handleToggleExpanded}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? 'Collapse work order flags' : 'Expand to add or manage work order flags'}
        className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center min-w-0">
            <svg className="h-5 w-5 mr-2 text-gray-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <span className="font-medium text-gray-900">Work Order Flags</span>
            {customFlags.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                {customFlags.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canManage && (
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                isExpanded
                  ? 'bg-gray-200 text-gray-700'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {isExpanded ? 'Hide options' : 'Add flags'}
              </span>
            )}
            <svg className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      <div className="px-4 py-3">
        {customFlags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {customFlags.map(flag => (
              <div key={flag.id} className="inline-flex items-center gap-1">
                <CustomFlagIndicator flag={flag} flagTemplates={templates} />
                {canManage && (
                  <button
                    type="button"
                    onClick={() => handleRemove(flag)}
                    disabled={removing}
                    className="text-gray-400 hover:text-red-600"
                    title="Remove flag"
                    aria-label={`Remove flag ${flag.flagValue}`}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-sm text-gray-500">No custom flags on this work order.</p>
            {canManage && !isExpanded && (
              <button
                type="button"
                onClick={handleToggleExpanded}
                className="inline-flex items-center self-start px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Add flags to this work order"
              >
                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Add flags
              </button>
            )}
          </div>
        )}

        {canManage && customFlags.length > 0 && !isExpanded && (
          <button
            type="button"
            onClick={handleToggleExpanded}
            className="mt-3 inline-flex items-center text-sm font-medium text-blue-700 hover:text-blue-800 focus:outline-none focus:underline"
            aria-label="Add more flags to this work order"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add more flags
          </button>
        )}
      </div>

      {isExpanded && canManage && (
        <div className="border-t border-gray-200 px-4 py-4 space-y-4">
          {availableTemplates.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Add from settings</h4>
              <div className="flex flex-wrap gap-2">
                {availableTemplates.map(template => {
                  const textColor = calculateTintedFlagTextColor(template.backgroundColor, 0.125);
                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => handleAddTemplateFlag(template)}
                      disabled={adding}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium hover:opacity-80 disabled:opacity-50"
                      style={{
                        backgroundColor: `${template.backgroundColor}20`,
                        color: textColor,
                        border: `1px solid ${template.backgroundColor}40`
                      }}
                    >
                      <FlagIcon iconName={template.iconName} className="h-4 w-4 mr-1" />
                      {template.flagValue}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Add one-off flag</h4>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={newFlag.flagValue}
                onChange={(event) => setNewFlag(prev => ({ ...prev, flagValue: event.target.value }))}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Flag text"
                aria-label="One-off flag text"
              />
              <input
                type="color"
                value={newFlag.flagColor}
                onChange={(event) => setNewFlag(prev => ({ ...prev, flagColor: event.target.value }))}
                className="h-10 w-14 border border-gray-300 rounded-md"
                title="Flag color"
                aria-label="Flag color"
              />
              <button
                type="button"
                onClick={handleAddManualFlag}
                disabled={adding || !newFlag.flagValue.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Add Flag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
